import { useState, useEffect, useMemo, useRef } from "react";
import type {
  GhostPost,
  GhostAuthor,
  GhostTag,
  SearchResult,
} from "../../types";
import { useAppContext } from "../../contexts/AppContext";
import AuthorResults from "./AuthorResults";
import TagResults from "./TagResults";
import PostResults from "./PostResults";

interface ResultsProps {
  posts: GhostPost[];
  authors: GhostAuthor[];
  tags: GhostTag[];
}

export default function Results({ posts, authors, tags }: ResultsProps) {
  const { searchValue } = useAppContext();
  const containerRef = useRef<HTMLDivElement>(null);

  const allResults = useMemo<SearchResult[]>(
    () => [...authors, ...tags, ...posts],
    [authors, tags, posts],
  );

  const [selectedResult, setSelectedResult] = useState<string | null>(
    allResults?.[0]?.id ?? null,
  );

  useEffect(() => {
    setSelectedResult(allResults?.[0]?.id ?? null);
  }, [allResults]);

  useEffect(() => {
    const handleKeyUp = (event: KeyboardEvent) => {
      const selectedIdx = allResults.findIndex((d) => d.id === selectedResult);
      const prevResult = allResults[selectedIdx - 1];
      const nextResult = allResults[selectedIdx + 1];

      if (event.key === "ArrowUp" && prevResult) {
        setSelectedResult(prevResult.id);
      } else if (event.key === "ArrowDown" && nextResult) {
        setSelectedResult(nextResult.id);
      } else if (event.key === "Enter") {
        const result = allResults.find((d) => d.id === selectedResult);
        if (result && "url" in result && result.url) {
          window.location.href = result.url;
        }
      }
    };

    const node = containerRef?.current;
    node?.ownerDocument.addEventListener("keyup", handleKeyUp);
    return () => node?.ownerDocument.removeEventListener("keyup", handleKeyUp);
  }, [allResults, selectedResult]);

  if (!searchValue) {
    return null;
  }

  return (
    <div
      className="overflow-y-auto max-h-[calc(100vh-172px)] sm:max-h-[70vh] -mt-[1px]"
      ref={containerRef}
    >
      <AuthorResults
        authors={authors}
        selectedResult={selectedResult}
        setSelectedResult={setSelectedResult}
      />
      <TagResults
        tags={tags}
        selectedResult={selectedResult}
        setSelectedResult={setSelectedResult}
      />
      <PostResults
        posts={posts}
        selectedResult={selectedResult}
        setSelectedResult={setSelectedResult}
      />
    </div>
  );
}
